import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { BlackGitHubIcon, WhiteGitHubIcon } from "./GitHubIcon";
import { Button } from "./ui/button";
import FadeIn from "./FadeIn";
import { AnimatedBlacksmithLogoOneline, BlacksmithLogomarkAnimationSVG } from "./BlacksmithLogoSVG";
import { usePostHog } from "posthog-js/react";
import { CORE_ANALYTICS_EVENTS } from "@/lib/posthog-constants";
import { useTheme } from "next-themes";
import { useSearchParams } from "next/navigation";
import { withFirstTouchRdtCid } from "@/lib/appFirstTouchAttribution";
import { isCodesmithExtensionEmbed } from "@/lib/codesmithExtensionEmbed";
export function LoginCard() {
  const $ = _c(14);
  const [isLoading, setIsLoading] = useState(false);
  const posthog = usePostHog();
  const {
    resolvedTheme
  } = useTheme();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== posthog || $[1] !== searchParams) {
    t0 = () => {
      setIsLoading(true);
      posthog?.capture(CORE_ANALYTICS_EVENTS.LOGIN_BUTTON_CLICK);
      const redirectUrl = encodeURIComponent(withFirstTouchRdtCid(window.location.href));
      const loginUrl = `${process.env.NEXT_PUBLIC_API_URL}/login/github?redirect=${redirectUrl}`;
      if (isCodesmithExtensionEmbed(searchParams.get("embed") ?? undefined)) {
        window.open(loginUrl, "_blank");
        setIsLoading(false);
        return;
      }
      window.location.href = loginUrl;
    };
    $[0] = posthog;
    $[1] = searchParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const handleLogin = t0;
  if (isLoading) {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex h-screen items-center justify-center"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="w-48 mx-auto py-4 mb-0 dark:text-blacksmith-yellow"><AnimatedBlacksmithLogoOneline /></div>;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="-mt-4 flex flex-col text-neutral-800 dark:text-white"><span className="text-xl font-medium">Sign in</span><span className="text-sm text-neutral-500 dark:text-neutral-400">to continue to Blacksmith</span></div>;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== resolvedTheme) {
    t3 = <>{resolvedTheme === "dark" ? <BlackGitHubIcon /> : <WhiteGitHubIcon />}Continue with GitHub</>;
    $[6] = resolvedTheme;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== handleLogin || $[9] !== t3) {
    t4 = <Button onClick={handleLogin} className="flex gap-2" variant="default" data-test="github-login-button">{t3}</Button>;
    $[8] = handleLogin;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="-mt-2 block text-sm text-neutral-500 dark:text-neutral-400">No account? We'll create one for you.</span>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4) {
    t6 = <div className="w-[360px] flex flex-col gap-6 text-center rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-8">{t1}{t2}{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
export default function SignedOut(t0) {
  const $ = _c(13);
  const {
    autoLogin: t1
  } = t0;
  const autoLogin = t1 === undefined ? false : t1;
  const searchParams = useSearchParams();
  let t2;
  if ($[0] !== searchParams) {
    t2 = searchParams.get("autoLogin");
    $[0] = searchParams;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const autoLoginParam = t2;
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);
  let t3;
  if ($[2] !== autoLogin || $[3] !== autoLoginParam || $[4] !== searchParams) {
    t3 = autoLoginParam === "false" ? false : autoLogin || searchParams.has("autoLogin");
    $[2] = autoLogin;
    $[3] = autoLoginParam;
    $[4] = searchParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const shouldAutoLogin = t3;
  let t4;
  let t5;
  if ($[6] !== isAutoLoggingIn || $[7] !== searchParams || $[8] !== shouldAutoLogin) {
    t4 = () => {
      if (isCodesmithExtensionEmbed(searchParams.get("embed") ?? undefined)) {
        return;
      }
      if (shouldAutoLogin && !isAutoLoggingIn) {
        setIsAutoLoggingIn(true);
        const redirectUrl = encodeURIComponent(withFirstTouchRdtCid(window.location.href));
        const loginUrl = `${process.env.NEXT_PUBLIC_API_URL}/login/github?redirect=${redirectUrl}`;
        window.location.href = loginUrl;
      } else {
        if (!shouldAutoLogin && isAutoLoggingIn) {
          setIsAutoLoggingIn(false);
        }
      }
    };
    t5 = [shouldAutoLogin, isAutoLoggingIn, searchParams];
    $[6] = isAutoLoggingIn;
    $[7] = searchParams;
    $[8] = shouldAutoLogin;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  if (isAutoLoggingIn) {
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex h-screen items-center justify-center bg-app-background"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex flex-col items-center justify-center min-h-screen bg-app-background"><FadeIn><LoginCard /></FadeIn></div>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}